import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Col, Row, Form, Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import useFieldClass from "../../../../functions/useFieldClass"; // Import the useFieldClass hook
import useDerateCalc from "../../../../functions/useDerateCalc";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectPumps, selectPumpDetails, selectFieldDescriptions, selectPumpManifoldPiping } from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice"; // Import the selector
import { selectGlobalVariables } from "../../../../redux/slices/globalVariablesSlice"; // Import the selector
import MaterialMismatchWarning from "../../../JobSchedulePage/MaterialMismatchWarning";
import { getMaterialMismatchData } from "../../../JobSchedulePage/utils/materialMatchUtils";
import { isValidTpl } from "../../../../utils/validateTpl";

const MainPump = ({ zone }) => {
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "pumpDetails";
  const parentSectionName = "pumps";
  const parentTableKey = parentSectionName;

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const parentUrl = apiUrls[parentSectionName]
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectPumps);
  const pumpDetails = useSelector(selectPumpDetails);
  const globalVariables = useSelector(selectGlobalVariables); // Fetch global variables
  const pumpManifoldPiping = useSelector(selectPumpManifoldPiping);
  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const jockeyReference = useMemo(() => {
    const jockeyUrlVariable = globalVariables.find(
      (variable) => variable.Title === "Jockey URL"
    );
    return jockeyUrlVariable?.VariableValue || "";
  }, [globalVariables]);

  const { formData, setFormData } = useFormState([]); // Initialize formData as an array
  const [, setTbdFields] = useState({});
  const [tplErrors, setTplErrors] = useState({});

  const elevationDerate = useDerateCalc();

  const mainPumpDetails = useMemo(() => {
    return pumpDetails.filter(
      (pump) =>
        pump.PumpID && // Add null check for PumpID
        ((pump.PumpID.toLowerCase() === "main" || pump.PumpID.toLowerCase() === "jockey") &&
        pump.Zone === activetab)
    );
  }, [pumpDetails, activetab]);

  const jockeyPumpDetails = useMemo(() => {
    if (pumpDetails.length === 1) {
      return [pumpDetails[0]];
    }

    const jockeyPump = pumpDetails.find(
      (pump) => pump.PumpID && pump.PumpID.toLowerCase() === "Jockey" && pump.Zone === activetab
    );

    if (jockeyPump) {
      return [jockeyPump];
    }

    const withJockeySequence = pumpDetails.find(
      (pump) => pump.JockeySequence && pump.Zone === activetab
    );

    if (withJockeySequence) {
      return [withJockeySequence];
    }

    const mainPump = pumpDetails.find(
      (pump) => pump.PumpID && pump.PumpID.toLowerCase() === "Main" && pump.Zone === activetab
    );

    if (mainPump) {
      return [mainPump];
    }

    return pumpDetails.filter((pump) => pump.Zone === activetab).slice(0, 1);
  }, [pumpDetails, activetab]);

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

    /****************Add dropdown options here*************************/

  const dischargeHeadMaterialOptions =
    (options?.filter((option) => option.DropdownType === "Discharge Head Material") || []).sort(
      (a, b) => a.SortOrder - b.SortOrder
    );
  const mainShaftMaterialOptions =
    (options?.filter((option) => option.DropdownType === "Main Shaft Material") || []).sort(
      (a, b) => a.SortOrder - b.SortOrder
    );
  const mainPumpSealTypeOptions =
    (options?.filter((option) => option.DropdownType === "Main Pump Seal Type") || []).sort(
      (a, b) => a.SortOrder - b.SortOrder
    );
  const mainCheckValveDesignOptions =
    (options?.filter((option) => option.DropdownType === "Main Check Valve Design") || []).sort(
      (a, b) => a.SortOrder - b.SortOrder
    );
  const jockeySequenceOptions =
    (options?.filter((option) => option.DropdownType === "Jockey Sequence") || []).sort(
      (a, b) => a.SortOrder - b.SortOrder
    );
  /******************************************************************/


  const { handleFocus, handleBlur, showNotification, saveStatus } = useHandleBlur(formData);

  const generatePumpLabels = (pumps) => {
  // Check if a jockey pump exists in the array
  const hasJockey = pumps.some(p => p.PumpID && p.PumpID.toLowerCase() === "jockey");
  let pumpCounter = hasJockey ? 2 : 1;

  // Order jockey pumps first, then main pumps, then others
  const sortedPumps = [...pumps].sort((a, b) => {
    const aType = a.PumpID ? a.PumpID.toLowerCase() : "";
    const bType = b.PumpID ? b.PumpID.toLowerCase() : "";
    if (aType === "jockey" && bType !== "jockey") return -1;
    if (aType !== "jockey" && bType === "jockey") return 1;
    if (aType === "main" && bType !== "main") return -1;
    if (aType !== "main" && bType === "main") return 1;
    // Otherwise, sort by VFD
    const aVFD = a.VFD ? parseInt(a.VFD.replace(/\D/g, "")) : Infinity;
    const bVFD = b.VFD ? parseInt(b.VFD.replace(/\D/g, "")) : Infinity;
    return aVFD - bVFD;
  });

  const updatedPumps = sortedPumps.map((pump) => {
    const newPump = { ...pump };
    if (newPump.PumpID && newPump.PumpID.toLowerCase() === "jockey") {
      newPump.DisplayPumpID = `Jockey Pump`;
    } else if (newPump.PumpID && newPump.PumpID.toLowerCase() === "main") {
      if (parseInt(newPump.Quantity) > 1) {
        const start = pumpCounter;
        const end = pumpCounter + parseInt(newPump.Quantity) - 1;
        newPump.DisplayPumpID = `Main Pumps ${start}-${end}`;
        pumpCounter = end + 1;
      } else {
        newPump.DisplayPumpID = `Main Pump ${pumpCounter}`;
        pumpCounter += 1;
      }
    } else {
      newPump.DisplayPumpID = `${newPump.PumpID || "Unknown"} Pump`;
    }
    return newPump;
  });
  return updatedPumps;
  };

  // Helper function to evaluate an entire form entry (a specific pump) for TBD fields
  const evaluateZoneTbdFields = useCallback((data) => {
    if (!data) return {};

    return Object.keys(data).reduce((acc, key) => {
      acc[key] = isFieldTbd(data[key]);
      return acc;
    }, {});
  }, []);

  const updatedMainPumpDetails = useMemo(
    () => generatePumpLabels(mainPumpDetails),
    [mainPumpDetails]
  );

  useEffect(() => {
    if (updatedMainPumpDetails.length > 0) {
      const formDataArray = updatedMainPumpDetails.map((mainPump) => ({
        ID: mainPump.ID || "",
        pumpWatertronicsPartNumber: mainPump.PumpWatertronicsPartNumber || "",
        pumpManufacturer: mainPump.PumpManufacturer || "",
        pumpManufacturerModelNumber: mainPump.PumpManufacturerModelNumber || "",
        pumpType: mainPump.PumpType || "",
        pumpEfficiency: mainPump.PumpEfficiency || "",
        pumpTDH: mainPump.PumpTDH || "",
        pumpFlow: mainPump.PumpFlow || "",
        pumpTPL: mainPump.PumpTPL || "",
        pumpStages: mainPump.PumpStages || "",
        pumpRPM: mainPump.PumpRPM || "",
        pumpDischargeSize: mainPump.PumpDischargeSize || "",
        pumpImpellerTrim: mainPump.PumpImpellerTrim || "",
        additionalCheckValve: mainPump.AdditionalCheckValve || "",
        motorWatertronicsPartNumber: mainPump.MotorWatertronicsPartNumber || "",
        motorManufacturer: mainPump.MotorManufacturer || "",
        motorManufacturerModelNumber:
          mainPump.MotorManufacturerModelNumber || "",
        motorHP: mainPump.MotorHP || "",
        motorEfficiency: mainPump.MotorEfficiency || "",
        motorEnclosure: mainPump.MotorEnclosure || "",
        motorFrame: mainPump.MotorFrame || "",
        motorFLA: mainPump.MotorFLA || "",
        motorCouplingType: mainPump.MotorCouplingType || "",
        motorCouplingSize: mainPump.MotorCouplingSize || "",
        motorWindingHeaters: mainPump.MotorWindingHeaters || "",
        motorTempSwitch: mainPump.MotorTempSwitch || "",
        motorVibrationSensor: mainPump.MotorVibrationSensor || "",
        comments: mainPump.Comments || "",
        quantity: mainPump.Quantity || "",
        elevationDerate: elevationDerate,
      }));
      setFormData(formDataArray);

      // Evaluate TBD fields for each pump
      const tbdFieldsArray = formDataArray.map(evaluateZoneTbdFields);
      setTbdFields(tbdFieldsArray);
    }
  }, [updatedMainPumpDetails, setFormData, elevationDerate, evaluateZoneTbdFields]);

  const [jockeyFormData, setJockeyFormData] = useState([]);
  const [allPumpData, setAllPumpData] = useState({});

  useEffect(() => {
    if (storedJobDetails.length > 0) {
      const jobDetail = storedJobDetails.find((detail) => detail.Zone === activetab) || {};
      const updatedAllPumpData = {
        ID: jobDetail.ID || "",
        motorEfficiencyForAllMotors: jobDetail.MotorEfficiencyForAllMotors || "",
        dischargeHeadMaterial: jobDetail.DischargeHeadMaterial || "",
        mainShaftMaterial: jobDetail.MainShaftMaterial || "",
        mainPumpSealType: jobDetail.MainPumpSealType || "",
        mainCheckValveDesign: jobDetail.MainCheckValveDesign || "",
        individualIsoValveDesign: jobDetail.IndividualIsoValveDesign || "",
      };
      setAllPumpData(updatedAllPumpData);
    }
  }, [storedJobDetails, activetab]);

  // Get pump manifold piping data for the active zone
  const zonePumpManifoldPiping = useMemo(() => {
    return pumpManifoldPiping.find((detail) => detail.Zone === activetab);
  }, [pumpManifoldPiping, activetab]);

  // Calculate material mismatch - use allPumpData for real-time validation
  const materialMismatch = useMemo(() => {
    // Create pump details array with current form data
    const currentPumpDetails = allPumpData.mainShaftMaterial ? [{
      MainShaftMaterial: allPumpData.mainShaftMaterial
    }] : mainPumpDetails;
    const result = getMaterialMismatchData(zonePumpManifoldPiping, currentPumpDetails);
    console.log('MainPump - Material Mismatch Check:', {
      columnMaterial: zonePumpManifoldPiping?.ColumnMaterialVT,
      shaftMaterial: allPumpData.mainShaftMaterial || mainPumpDetails[0]?.MainShaftMaterial,
      zonePumpManifoldPiping,
      currentPumpDetails,
      result
    });
    return result;
  }, [allPumpData.mainShaftMaterial, zonePumpManifoldPiping, mainPumpDetails]);

  const jockeySequence = useMemo(() => {
    if (jockeyPumpDetails.length === 1) {
      return jockeyPumpDetails[0];
    }
  
    // First try to find a pump with PumpID of "Jockey" in the current zone
    const jockeyPump = jockeyPumpDetails.find(
      (pump) => pump.PumpID && pump.PumpID.toLowerCase() === "jockey" && pump.Zone === activetab
    );
  
    if (jockeyPump) {
      return jockeyPump;
    }
  
    // If not found, look for any pump with JockeySequence defined in the current zone
    const withJockeySequence = jockeyPumpDetails.find(
      (pump) => pump.JockeySequence && pump.Zone === activetab
    );
  
    if (withJockeySequence) {
      return withJockeySequence;
    }
  
    // If not found, look for a pump with PumpID of "Main" in the current zone
    const mainPump = jockeyPumpDetails.find(
      (pump) => pump.PumpID && pump.PumpID.toLowerCase() === "main" && pump.Zone === activetab
    );
  
    if (mainPump) {
      return mainPump;
    }
  
    // If not found, use any other available pump in the current zone or first pump
    return jockeyPumpDetails[0] || {};
  }, [jockeyPumpDetails, activetab]);

  useEffect(() => {
    if (jockeySequence && Object.keys(jockeySequence).length > 0) {
      setJockeyFormData([
        {
          ID: jockeySequence.ID || "",
          jockeySequence: jockeySequence.JockeySequence || "",
          // Any other jockey-specific fields needed
        },
      ]);
    }
  }, [jockeySequence, setJockeyFormData]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
    const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
    const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
    const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

// Helper function to check if any field in the form has a "TBD" value, even if it's part of a larger string
const isFieldTbd = (fieldValue) => {
  if (typeof fieldValue === 'string') {
    fieldValue = fieldValue.trim().toLowerCase(); // Normalize to lowercase and remove extra whitespace
    return fieldValue.includes("tbd"); // Check if "tbd" is present anywhere in the string
  }
  return false;
};

// Evaluate TBD fields for formDataArray, allPumpData, and jockeyPumpData
const formDataTbdFields = Array.isArray(formData) ? formData.map(evaluateZoneTbdFields) : [];

const isValueInDropdown = (value, dropdownOptions) => {
  const exists = dropdownOptions.some(
    (option) => option.DisplayText === value || option.displayName === value
  );
  return exists;
};

  const handleChange = (e, pumpIndex) => {
    const { name, value } = e.target;
    if (pumpIndex !== undefined) {
      setFormData((prevData) => ({
        ...prevData,
        [pumpIndex]: {
          ...prevData[pumpIndex],
          [name]: value,
        },
      }));
    } else if (name === "jockeySequence") {
      setJockeyFormData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData.length > 0) {
          updatedData[0] = { ...updatedData[0], [name]: value };
        } else {
          updatedData.push({ [name]: value });
        }
        return updatedData;
      });
    } else {
      setAllPumpData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const renderPumpDetails = (pump, index) => {
    // Check if this pump has any TBD fields
    const pumpHasTbd = formDataTbdFields[index] ? 
      Object.values(formDataTbdFields[index]).some(value => value === true) 
      : false;
      
    return (
    <Accordion.Item eventKey={index.toString()} key={pump.ID}>
      <Accordion.Header>
        {pumpHasTbd && (
          <i className="bi bi-exclamation-diamond-fill me-2" style={{ color: "red" }} />
        )}
        {pump.DisplayPumpID}
      </Accordion.Header>
      <Accordion.Body>
        <div>
          <Row>
            {/* ******* Pump ******* */}
            <Col>
              <h4 className="subsection-header mb-3">Pump Details</h4>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`pumpWatertronicsPartNumber-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpWatertronicsPartNumber")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Watertronics Part Number</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpWatertronicsPartNumber"
                        value={
                          formData[index]?.pumpWatertronicsPartNumber || ""
                        }
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Watertronics Part Number", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpWatertronicsPartNumber",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpWatertronicsPartNumber
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Watertronics Part Number"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpManufacturer-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpManufacturer")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Manufacturer</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpManufacturer"
                        value={formData[index]?.pumpManufacturer || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Manufacturer", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpManufacturer",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpManufacturer
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Manufacturer"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`pumpManufacturerModelNumber-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpManufacturerModelNumber")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Manufacturer Model Number</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpManufacturerModelNumber"
                        value={
                          formData[index]?.pumpManufacturerModelNumber || ""
                        }
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Manufacturer Model Number", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpManufacturerModelNumber",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpManufacturerModelNumber
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Manufacturer Model Number"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpType-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpType")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Type</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpType"
                        value={formData[index]?.pumpType || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Type", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpType",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpType ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Type"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpEfficiency-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpEfficiency")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Efficiency</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpEfficiency"
                        value={formData[index]?.pumpEfficiency || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Efficiency", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpEfficiency",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpEfficiency ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Efficiency"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpTDH-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpTDH")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>TDH</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpTDH"
                        value={formData[index]?.pumpTDH || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump TDH", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpTDH",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpTDH ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter TDH"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpFlow-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpFlow")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Flow</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpFlow"
                        value={formData[index]?.pumpFlow || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Flow", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpFlow",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpFlow ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Flow"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpTPL-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpTPL")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>TPL (in)</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpTPL"
                        value={formData[index]?.pumpTPL || ""}
                        onFocus={handleFocus}
                        onBlur={(e) => {
                          const val = e.target.value;
                          if (!isValidTpl(val)) {
                            return;
                          }
                          handleBlur(e, "Pump TPL", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpTPL",
                            tableKey,
                          });
                        }}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          `${formDataTbdFields[index]?.pumpTPL ? "tbd-highlight" : ""} ${tplErrors[index] ? "is-invalid" : ""}`
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          setTplErrors((prev) => ({ ...prev, [index]: !isValidTpl(val) }));
                          handleChange(e, index);
                        }}
                        placeholder="Enter TPL"
                      />
                      {tplErrors[index] && (
                        <Form.Text className="text-danger">
                          Enter "TBD" or a numeric value
                        </Form.Text>
                      )}
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpStages-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpStages")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Stages</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpStages"
                        value={formData[index]?.pumpStages || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Stages", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpStages",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpStages ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Stages"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpRPM-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpRPM")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>RPM</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpRPM"
                        value={formData[index]?.pumpRPM || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump RPM", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpRPM",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpRPM ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter RPM"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpDischargeSize-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpDischargeSize")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Discharge Size</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpDischargeSize"
                        value={formData[index]?.pumpDischargeSize || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Discharge Size", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpDischargeSize",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpDischargeSize
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Discharge Size"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`pumpImpellerTrim-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PumpImpellerTrim")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Impeller Trim</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="pumpImpellerTrim"
                        value={formData[index]?.pumpImpellerTrim || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Pump Impeller Trim", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "PumpImpellerTrim",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.pumpImpellerTrim ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Impeller Trim"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`quantity-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Quantity")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Quantity</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="quantity"
                        value={formData[index]?.quantity || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Quantity", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "Quantity",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.quantity ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Quantity"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            {/* ******* Motor ******* */}
            <Col>
              <h4 className="subsection-header mb-3">Motor Details</h4>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`motorWatertronicsPartNumber-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorWatertronicsPartNumber")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Watertronics Part Number</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorWatertronicsPartNumber"
                        value={
                          formData[index]?.motorWatertronicsPartNumber || ""
                        }
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Watertronics Part Number", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorWatertronicsPartNumber",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorWatertronicsPartNumber
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Watertronics Part Number"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorManufacturer-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorManufacturer")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Manufacturer</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorManufacturer"
                        value={formData[index]?.motorManufacturer || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Manufacturer", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorManufacturer",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorManufacturer
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Manufacturer"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`motorManufacturerModelNumber-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorManufacturerModelNumber")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Manufacturer Model Number</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorManufacturerModelNumber"
                        value={
                          formData[index]?.motorManufacturerModelNumber || ""
                        }
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Manufacturer Model Number", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorManufacturerModelNumber",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorManufacturerModelNumber
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Manufacturer Model Number"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorHP-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorHP")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>HP</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorHP"
                        value={formData[index]?.motorHP || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor HP", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorHP",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorHP ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter HP"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorEfficiency-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorEfficiency")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Efficiency</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorEfficiency"
                        value={formData[index]?.motorEfficiency || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Efficiency", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorEfficiency",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorEfficiency ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Efficiency"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorEnclosure-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorEnclosure")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Enclosure</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorEnclosure"
                        value={formData[index]?.motorEnclosure || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Enclosure", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorEnclosure",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorEnclosure ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Enclosure"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorFrame-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorFrame")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Frame</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorFrame"
                        value={formData[index]?.motorFrame || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Frame", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorFrame",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorFrame ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Frame"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorFLA-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorFLA")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>FLA</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorFLA"
                        value={formData[index]?.motorFLA || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor FLA", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorFLA",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorFLA ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter FLA"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorCouplingType-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorCouplingType")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Coupling Type</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorCouplingType"
                        value={formData[index]?.motorCouplingType || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Coupling Type", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorCouplingType",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorCouplingType
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Coupling Type"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorCouplingSize-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorCouplingSize")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Coupling Size</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorCouplingSize"
                        value={formData[index]?.motorCouplingSize || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Coupling Size", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorCouplingSize",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorCouplingSize
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Coupling Size"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`motorWindingHeaters-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorWindingHeaters")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Winding Heaters</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorWindingHeaters"
                        value={formData[index]?.motorWindingHeaters || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Winding Heaters", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorWindingHeaters",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorWindingHeaters
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Winding Heaters"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId={`motorTempSwitch-${index}`}>
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorTempSwitch")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Temp Switch</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorTempSwitch"
                        value={formData[index]?.motorTempSwitch || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Temp Switch", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorTempSwitch",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorTempSwitch ? "tbd-highlight" : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Temp Switch"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group
                    as={Row}
                    controlId={`motorVibrationSensor-${index}`}
                  >
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("MotorVibrationSensor")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Vibration Sensor</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        name="motorVibrationSensor"
                        value={formData[index]?.motorVibrationSensor || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Motor Vibration Sensor", [], {
                            ID: pump.ID,
                            url: apiUrl,
                            field: "MotorVibrationSensor",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                        className={
                          formDataTbdFields[index]?.motorVibrationSensor
                            ? "tbd-highlight"
                            : ""
                        }
                        onChange={(e) => handleChange(e, index)}
                        placeholder="Enter Vibration Sensor"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId={`comments-${index}`}>
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Comments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comments"
                  value={formData[index]?.comments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Comments", [], {
                      ID: pump.ID,
                      url: apiUrl,
                      field: "Comments",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                  className={
                    formDataTbdFields[index]?.comments ? "tbd-highlight" : ""
                  }
                  onChange={(e) => handleChange(e, index)}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
};

  const fieldClasses = useFieldClass(allPumpData, zone); // Use useFieldClass hook for fields outside the accordion

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="elevationDerate">
                  <Form.Label column md={3}>
                    Elevation Derate
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="elevationDerate"
                      value={formData.elevationDerate || ""}
                      disabled
                      placeholder={elevationDerate}
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col />
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorEfficiencyForAllMotors">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorEfficiencyForAllMotors")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Motor Efficiency For All Motors</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorEfficiencyForAllMotors"
                      value={allPumpData.motorEfficiencyForAllMotors || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Efficiency For All Motors", [], {
                          ID: allPumpData.ID,
                          url: parentUrl,
                          field: "MotorEfficiencyForAllMotors",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={fieldClasses.motorEfficiencyForAllMotors}
                      onChange={handleChange}
                      placeholder="Enter Motor Efficiency For All Motors"
                    />
                                </Col>
                              </Form.Group>
                              </Col>
                              <Col>
                              <Form.Group as={Row} controlId="jockeySequence">
                                <Form.Label column md={3}>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={renderTooltip("JockeySequence")}
                                  delay={{ show: 300, hide: 200 }}
                                >
                                  <a href={jockeyReference} target="_blank" rel="noopener noreferrer">
                                  Jockey Sequence
                                  </a>
                                </OverlayTrigger>
                                </Form.Label>
                                <Col>
                                <Form.Select
                                  name="jockeySequence"
                                  value={jockeyFormData[0]?.jockeySequence || ""}
                                  onFocus={handleFocus}
                                  onBlur={(e) =>
                                  handleBlur(e, "Jockey Sequence", jockeySequenceOptions, {
                                    ID: jockeyFormData[0]?.ID,
                                    url: apiUrl,
                                    field: "JockeySequence",
                                    tableKey,
                                  })
                                  }
                                  disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                                  className={
                                    isFieldTbd(jockeyFormData[0]?.jockeySequence)
                                      ? "tbd-highlight"
                                      : fieldClasses.jockeySequence
                                  }
                      onChange={handleChange}
                    >
                      <option value="">Select Jockey Sequence</option>
                      {jockeySequenceOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        jockeyFormData[0]?.jockeySequence,
                        jockeySequenceOptions
                      ) &&
                        jockeyFormData[0]?.jockeySequence && (
                          <option
                            value={jockeyFormData[0]?.jockeySequence}
                            style={{ color: "red" }}
                          >
                            *{jockeyFormData[0]?.jockeySequence}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </div>
          <div>
            <Row className="mb-3">
              <Col>
                <Accordion defaultActiveKey="0">
                  {updatedMainPumpDetails.map((pump, index) =>
                    renderPumpDetails(pump, index)
                  )}
                </Accordion>
              </Col>
            </Row>
            <Col />
          </div>
          <div>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="dischargeHeadMaterial">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("DischargeHeadMaterial")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Discharge Head Material</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="dischargeHeadMaterial"
                      value={allPumpData.dischargeHeadMaterial || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Discharge Head Material",
                          dischargeHeadMaterialOptions,
                          {
                            ID: allPumpData.ID,
                            url: parentUrl,
                            field: "DischargeHeadMaterial",
                            tableKey: parentTableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={fieldClasses.dischargeHeadMaterial} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Discharge Head Material</option>
                      {dischargeHeadMaterialOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        allPumpData.dischargeHeadMaterial,
                        dischargeHeadMaterialOptions
                      ) &&
                        allPumpData.dischargeHeadMaterial && (
                          <option
                            value={allPumpData.dischargeHeadMaterial}
                            style={{ color: "red" }}
                          >
                            *{allPumpData.dischargeHeadMaterial}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="mainShaftMaterial">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MainShaftMaterial")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Main Shaft Material</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="mainShaftMaterial"
                      value={allPumpData.mainShaftMaterial || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Main Shaft Material",
                          mainShaftMaterialOptions,
                          {
                            ID: allPumpData.ID,
                            url: parentUrl,
                            field: "MainShaftMaterial",
                            tableKey: parentTableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={fieldClasses.mainShaftMaterial} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Main Shaft Material</option>
                      {mainShaftMaterialOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        allPumpData.mainShaftMaterial,
                        mainShaftMaterialOptions
                      ) &&
                        allPumpData.mainShaftMaterial && (
                          <option
                            value={allPumpData.mainShaftMaterial}
                            style={{ color: "red" }}
                          >
                            *{allPumpData.mainShaftMaterial}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                  <Col md={12}>
                    <MaterialMismatchWarning 
                      mismatchData={materialMismatch} 
                      fieldType="shaft" 
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="mainPumpSealType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MainPumpSealType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Main Pump Seal Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="mainPumpSealType"
                      value={allPumpData.mainPumpSealType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Main Pump Seal Type",
                          mainPumpSealTypeOptions,
                          {
                            ID: allPumpData.ID,
                            url: parentUrl,
                            field: "MainPumpSealType",
                            tableKey: parentTableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={fieldClasses.mainPumpSealType} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Main Pump Seal Type</option>
                      {mainPumpSealTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        allPumpData.mainPumpSealType,
                        mainPumpSealTypeOptions
                      ) &&
                        allPumpData.mainPumpSealType && (
                          <option
                            value={allPumpData.mainPumpSealType}
                            style={{ color: "red" }}
                          >
                            *{allPumpData.mainPumpSealType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="mainCheckValveDesign">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MainCheckValveDesign")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Main Check Valve Design</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="mainCheckValveDesign"
                      value={allPumpData.mainCheckValveDesign || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Main Check Valve Design",
                          mainCheckValveDesignOptions,
                          {
                            ID: allPumpData.ID,
                            url: parentUrl,
                            field: "MainCheckValveDesign",
                            tableKey: parentTableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={fieldClasses.mainCheckValveDesign} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Main Check Valve Design</option>
                      {mainCheckValveDesignOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        allPumpData.mainCheckValveDesign,
                        mainCheckValveDesignOptions
                      ) &&
                        allPumpData.mainCheckValveDesign && (
                          <option
                            value={allPumpData.mainCheckValveDesign}
                            style={{ color: "red" }}
                          >
                            *{allPumpData.mainCheckValveDesign}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPump;
